import { useMediaQuery } from 'academy-common-lib/utils/Styling';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { PlaylistDragProvider, PlaylistItem } from './PlaylistItem';
import { PlaylistItemManager, usePlaylistItemManager } from './PlaylistItemManager';
import { PlaylistItemSearchManager, PlaylistItemSearchProvider, usePlaylistItemSearch } from './PlaylistItemSearchManager';
import { useRef } from 'react';
import { useCanUserEditPlaylist } from 'academy-common-lib/features/savedItems/savedItemsHooks';
import ApiError from '../../../components/Errors/ApiError';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/transitional/components/View';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { Button } from 'foundations-components/transitional/components/Button';
import { AddItemsToPlaylistModal } from './AddItemsToPlaylistModal';
import UsageTracker from '../../../utils/UsageTracker';
import { EditPlaylistAIButton, GeneratePlaylistAIButton } from './PlaylistAIButton';
import { usePlaylistBreezeSession } from '../../myPlaylists/hooks/usePlaylistBreezeSession';
import { HasGate } from '../../accounts/components/HasGate';
import { Flex } from 'foundations-components/components/Flex';
import { usePlaylistItemCardModels } from 'academy-common-lib/features/savedItems/savedItemsHooks';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const PlaylistItemCount = () => {
  const {
    numberOfSelectedItems: selectedExistingPlaylistItems
  } = usePlaylistItemManager();
  const {
    numberOfSelectedItems: selectedNewPlaylistItems
  } = usePlaylistItemSearch();
  const count = selectedNewPlaylistItems > 0 ? selectedNewPlaylistItems : selectedExistingPlaylistItems;
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: "learningCenter.savedItems.playlistDetails.numberOfPlaylistItems",
    options: {
      count
    }
  });
};
const PlaylistBreezeSessionSetup = ({
  mode,
  children
}) => {
  const {
    playlistItems,
    saveNewPlaylistItems
  } = usePlaylistItemManager();
  usePlaylistBreezeSession(playlistItems, saveNewPlaylistItems, mode);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: children
  });
};
const PlaylistItemsHeader = ({
  playlistItemsCount,
  onAddToPlaylistClick
}) => {
  const {
    isMobile
  } = useMediaQuery();
  if (!playlistItemsCount) {
    return /*#__PURE__*/_jsx(PlaylistItemCount, {});
  }
  return /*#__PURE__*/_jsx(TFlex, {
    align: "center",
    justify: "between",
    children: /*#__PURE__*/_jsxs(List, {
      marginBlockEnd: "400",
      use: "inline-divided",
      children: [/*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(PlaylistItemCount, {})
      }), /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "learningCenter.savedItems.playlistDetails.dragToReorder"
        })
      }), /*#__PURE__*/_jsx(ListItem, {
        children: /*#__PURE__*/_jsx(Button, {
          "data-test-id": "add-to-playlist-link-button",
          onClick: onAddToPlaylistClick,
          paddingInline: "0",
          use: "transparent",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "learningCenter.savedItems.playlistDetails.addToPlaylist"
          })
        })
      }), /*#__PURE__*/_jsx(HasGate, {
        gate: "Academy:AIPlaylistGeneration",
        children: !isMobile && /*#__PURE__*/_jsx(ListItem, {
          children: /*#__PURE__*/_jsx(EditPlaylistAIButton, {
            variant: "inline"
          })
        })
      })]
    })
  });
};
const PlaylistItemSearchManagerList = ({
  playlistId,
  playlistItems
}) => {
  const {
    cardModels,
    cardModelsError,
    cardModelsLoading,
    userProgressDataLoading
  } = usePlaylistItemCardModels({
    playlistItems
  });
  if (cardModelsError) return /*#__PURE__*/_jsx(ApiError, {
    error: cardModelsError
  });
  return /*#__PURE__*/_jsx(PlaylistDragProvider, {
    children: /*#__PURE__*/_jsx(View, {
      marginBlockStart: "400",
      children: /*#__PURE__*/_jsx(List, {
        "data-test-id": "playlist-items-list",
        ordered: true,
        children: playlistItems.map((playlistItem, i) => {
          const cardModel = cardModels === null || cardModels === void 0 ? void 0 : cardModels.find(({
            id,
            type,
            language
          }) => id === playlistItem.itemId && type === playlistItem.type && language === playlistItem.language);
          return /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(PlaylistItem, {
              cardModel: cardModel,
              isLoadingCardModel: cardModelsLoading || userProgressDataLoading,
              playlistId: playlistId,
              playlistItem: playlistItem,
              playlistLength: playlistItems.length,
              position: i
            })
          }, playlistItem.itemId);
        })
      })
    })
  });
};
export const PlaylistItems = ({
  isAddItemsModalOpen,
  playlistId,
  setIsAddItemsModalOpen
}) => {
  const {
    isMobile
  } = useMediaQuery();
  const userCanEditPlaylist = useCanUserEditPlaylist(playlistId);
  const scrollRef = useRef(null);
  const openAddItemsModal = () => {
    setIsAddItemsModalOpen(true);
    UsageTracker.trackStandalone('Playlist Interaction', {
      action: 'open-add-to-playlist-modal'
    });
  };
  return /*#__PURE__*/_jsx(PlaylistItemManager, {
    playlistId: playlistId,
    render: playlistItems => {
      const hasPlaylistItems = playlistItems.length > 0;
      return /*#__PURE__*/_jsxs(PlaylistBreezeSessionSetup, {
        mode: playlistItems.length > 0 ? 'edit' : 'create',
        children: [/*#__PURE__*/_jsx("div", {
          ref: scrollRef,
          children: /*#__PURE__*/_jsx(Text, {
            as: isMobile ? 'h5' : 'h4',
            marginBlockEnd: "100",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "learningCenter.savedItems.playlistDetails.playlistItems"
            })
          })
        }), /*#__PURE__*/_jsxs(PlaylistItemSearchProvider, {
          playlistId: playlistId,
          children: [userCanEditPlaylist && /*#__PURE__*/_jsx(PlaylistItemsHeader, {
            onAddToPlaylistClick: openAddItemsModal,
            playlistItemsCount: playlistItems.length
          }), /*#__PURE__*/_jsx(PlaylistItemSearchManager, {
            listView: /*#__PURE__*/_jsxs(_Fragment, {
              children: [hasPlaylistItems && /*#__PURE__*/_jsx(PlaylistItemSearchManagerList, {
                playlistId: playlistId,
                playlistItems: playlistItems
              }), userCanEditPlaylist && /*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                marginBlockStart: "600",
                children: [/*#__PURE__*/_jsx(Button, {
                  "data-test-id": "add-to-playlist-primary-button",
                  onClick: openAddItemsModal,
                  use: "primary",
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "learningCenter.savedItems.playlistDetails.addToPlaylist"
                  })
                }), /*#__PURE__*/_jsx(HasGate, {
                  gate: "Academy:AIPlaylistGeneration",
                  children: hasPlaylistItems ? /*#__PURE__*/_jsx(EditPlaylistAIButton, {}) : /*#__PURE__*/_jsx(GeneratePlaylistAIButton, {})
                })]
              })]
            })
          })]
        }), /*#__PURE__*/_jsx(AddItemsToPlaylistModal, {
          isOpen: isAddItemsModalOpen,
          onClose: () => setIsAddItemsModalOpen(false),
          playlistId: playlistId
        })]
      });
    }
  });
};